import React, { useState } from "react";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../service/firebase";
import bcrypt from "bcryptjs";
import "./BuyerDeleteAccountPlayStore.css";

const BuyerDeleteAccountPlayStore = () => {
  const [step, setStep] = useState(1); // 1: login, 2: verification, 3: confirmation
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [buyerInfo, setBuyerInfo] = useState(null);

  // Step 1: Login with mobile and password
  const handleLogin = async () => {
    if (!mobile || mobile.length !== 10 || isNaN(mobile)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!password) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Find buyer by mobile number
      const q = query(collection(db, "buyers"), where("mobile", "==", mobile));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("No JMI Buyer account found with this mobile number");
        setLoading(false);
        return;
      }

      // Get the first matching buyer (should be only one)
      const buyerDoc = querySnapshot.docs[0];
      const buyerData = buyerDoc.data();

      // Verify password
      const isPasswordValid = await bcrypt.compare(password, buyerData.passwordHash);
      if (!isPasswordValid) {
        setError("Invalid password. Please try again.");
        setLoading(false);
        return;
      }

      // Store buyer info and proceed to verification step
      setBuyerInfo({
        buyerId: buyerDoc.id,
        mobile: buyerData.mobile,
        createdAt: buyerData.createdAt
      });
      setStep(2);
      setError("");
    } catch (err) {
      console.error("Login error:", err);
      setError("Failed to verify account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Final confirmation and deletion
  const deleteAccountPermanently = async () => {
    if (confirmText !== "DELETE MY ACCOUNT") {
      setError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    if (!buyerInfo) {
      setError("Account information not found. Please start over.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const batch = writeBatch(db);
      const { buyerId, mobile } = buyerInfo;

      // Verify account still exists before deletion
      const buyerRef = doc(db, "buyers", buyerId);
      const buyerSnap = await getDoc(buyerRef);
      
      if (!buyerSnap.exists()) {
        setError("Account not found. It may have been already deleted.");
        setLoading(false);
        return;
      }

      // 1. Delete buyer's main document
      batch.delete(buyerRef);

      // 2. Delete buyer's orders
      const ordersQuery = query(
        collection(db, "orders"),
        where("buyerId", "==", buyerId)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      ordersSnapshot.forEach((orderDoc) => {
        batch.delete(orderDoc.ref);
      });

      // 3. Delete buyer's cart items
      const cartQuery = query(
        collection(db, "cart"),
        where("buyerId", "==", buyerId)
      );
      const cartSnapshot = await getDocs(cartQuery);
      cartSnapshot.forEach((cartDoc) => {
        batch.delete(cartDoc.ref);
      });

      // 4. Delete buyer's addresses
      const addressesQuery = query(
        collection(db, "addresses"),
        where("buyerId", "==", buyerId)
      );
      const addressesSnapshot = await getDocs(addressesQuery);
      addressesSnapshot.forEach((addressDoc) => {
        batch.delete(addressDoc.ref);
      });

      // 5. Delete any OTPs associated with this mobile
      const otpQuery = query(
        collection(db, "otps"),
        where("mobile", "==", `+91${mobile}`)
      );
      const otpSnapshot = await getDocs(otpQuery);
      otpSnapshot.forEach((otpDoc) => {
        batch.delete(otpDoc.ref);
      });

      // 6. Delete buyer's favorites/wishlist
      try {
        const favoritesQuery = query(
          collection(db, "favorites"),
          where("buyerId", "==", buyerId)
        );
        const favoritesSnapshot = await getDocs(favoritesQuery);
        favoritesSnapshot.forEach((favDoc) => {
          batch.delete(favDoc.ref);
        });
      } catch (err) {
        console.log("No favorites to delete");
      }

      // 7. Delete buyer's notifications if any
      try {
        const notificationsQuery = query(
          collection(db, "notifications"),
          where("buyerId", "==", buyerId)
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);
        notificationsSnapshot.forEach((notifDoc) => {
          batch.delete(notifDoc.ref);
        });
      } catch (err) {
        console.log("No notifications to delete");
      }

      // Execute all deletions
      await batch.commit();

      // Show success and reset
      setStep(3);
      setError("");
      
    } catch (err) {
      console.error("Account deletion error:", err);
      setError("Failed to delete account. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  // Reset form to start over
  const resetForm = () => {
    setStep(1);
    setMobile("");
    setPassword("");
    setConfirmText("");
    setBuyerInfo(null);
    setError("");
  };

  return (
    <div className="buyer-delete-wrapper">
      <div className="buyer-delete-container">
        <header className="buyer-app-header">
          <h1 className="buyer-app-name">JMI Buyer</h1>
          <p className="buyer-app-tagline">Account Deletion Request</p>
        </header>

        {/* Data Deletion Information */}
        <section className="buyer-data-deletion-info">
          <h2>📱 JMI Buyer Account Deletion Process</h2>
          <p>
            To request deletion of your JMI Buyer account and associated data, 
            please follow the steps below. This process is permanent and irreversible.
          </p>

          <div className="buyer-data-types">
            <div className="buyer-data-section">
              <h3>✅ Data That Will Be Deleted Immediately</h3>
              <ul>
                <li>Your personal profile information (name, mobile number, email)</li>
                <li>Account credentials and login information</li>
                <li>All your order history and purchase records</li>
                <li>Saved shipping and billing addresses</li>
                <li>Shopping cart items and saved baskets</li>
                <li>Wishlist and favorite products</li>
                <li>Notification preferences and settings</li>
                <li>OTP verification records</li>
                <li>Search history and browsing preferences</li>
                <li>All other JMI Buyer account-associated data</li>
              </ul>
            </div>

            <div className="buyer-data-section">
              <h3>⏳ Data Retention & Legal Compliance</h3>
              <ul>
                <li><strong>Immediate Deletion:</strong> Most personal data is deleted immediately upon account deletion request</li>
                <li><strong>30-Day Backup Period:</strong> Some data may remain in secure backups for up to 30 days for security purposes</li>
                <li><strong>Legal Requirements:</strong> Certain financial transaction records may be retained as required by Indian laws and regulations</li>
                <li><strong>Fraud Prevention:</strong> Minimal data may be retained to prevent fraud and maintain platform security</li>
              </ul>
            </div>

            <div className="buyer-data-section">
              <h3>📋 Steps to Delete Your Account</h3>
              <ol>
                <li>Enter your registered mobile number and password</li>
                <li>Verify your account ownership</li>
                <li>Confirm deletion by typing "DELETE MY ACCOUNT"</li>
                <li>Receive confirmation of account deletion</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Account Deletion Form */}
        <section className="buyer-deletion-form-section">
          <h2>🔐 Request JMI Buyer Account Deletion</h2>
          
          {step === 1 && (
            <div className="buyer-login-step">
              <h3>Step 1: Verify Your JMI Buyer Account</h3>
              <p>Enter your registered mobile number and password to continue with account deletion.</p>
              
              <div className="buyer-form-group">
                <label htmlFor="buyerMobile">Registered Mobile Number</label>
                <input
                  id="buyerMobile"
                  type="tel"
                  placeholder="Enter your 10-digit mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  maxLength="10"
                  disabled={loading}
                />
              </div>

              <div className="buyer-form-group">
                <label htmlFor="buyerPassword">Account Password</label>
                <input
                  id="buyerPassword"
                  type="password"
                  placeholder="Enter your JMI Buyer account password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleLogin();
                  }}
                />
              </div>

              {error && <div className="buyer-error-message">{error}</div>}

              <button
                className="buyer-verify-btn"
                onClick={handleLogin}
                disabled={loading || !mobile || mobile.length !== 10 || !password}
              >
                {loading ? "Verifying JMI Buyer Account..." : "Verify Account"}
              </button>
            </div>
          )}

          {step === 2 && buyerInfo && (
            <div className="buyer-confirmation-step">
              <h3>Step 2: Final Confirmation</h3>
              <div className="buyer-account-summary">
                <p><strong>JMI Buyer Account:</strong> +91 {buyerInfo.mobile}</p>
                <p><strong>Buyer ID:</strong> {buyerInfo.buyerId}</p>
                <p><strong>Account Created:</strong> {buyerInfo.createdAt?.toDate?.().toLocaleDateString() || 'Unknown'}</p>
              </div>
              
              <div className="buyer-final-warning">
                <h4>⚠️ Final Warning - Permanent Action</h4>
                <p>
                  You are about to <strong>permanently delete</strong> your JMI Buyer account. 
                  This action <strong>cannot be undone or reversed</strong>. All your data including 
                  order history, addresses, and preferences will be permanently erased as described above.
                </p>
              </div>

              <div className="buyer-form-group">
                <label htmlFor="buyerConfirmText">
                  To confirm account deletion, please type <strong>"DELETE MY ACCOUNT"</strong> exactly as shown:
                </label>
                <input
                  id="buyerConfirmText"
                  type="text"
                  placeholder='Type "DELETE MY ACCOUNT" to confirm permanent deletion'
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && confirmText === "DELETE MY ACCOUNT") {
                      deleteAccountPermanently();
                    }
                  }}
                />
              </div>

              {error && <div className="buyer-error-message">{error}</div>}

              <div className="buyer-button-group">
                <button
                  className="buyer-back-btn"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Go Back
                </button>
                <button
                  className="buyer-delete-btn"
                  onClick={deleteAccountPermanently}
                  disabled={loading || confirmText !== "DELETE MY ACCOUNT"}
                >
                  {loading ? "Deleting JMI Buyer Account..." : "Permanently Delete My Account"}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="buyer-success-step">
              <div className="buyer-success-icon">✅</div>
              <h3>JMI Buyer Account Deletion Successful</h3>
              <p>
                Your JMI Buyer account and all associated personal data have been scheduled for deletion. 
              </p>
              <div className="buyer-deletion-summary">
                <p><strong>What happens next:</strong></p>
                <ul>
                  <li>Most personal data has been deleted immediately</li>
                  <li>Remaining data in backups will be purged within 30 days</li>
                  <li>You will no longer be able to access your account</li>
                  <li>All your order history and preferences have been removed</li>
                </ul>
              </div>
              <p>
                <strong>Thank you for being a JMI Buyer. We hope to serve you again in the future.</strong>
              </p>
              <button
                className="buyer-new-request-btn"
                onClick={resetForm}
              >
                Delete Another Account
              </button>
            </div>
          )}
        </section>

        {/* Support Information */}
        <section className="buyer-support-section">
          <h3>Need Help with Account Deletion?</h3>
          <p>
            If you encounter any issues during the account deletion process or have questions 
            about your data, our support team is here to help:
          </p>
          {/* <div className="buyer-support-contact">
            <img 
              src="/whatsapp.svg" 
              alt="WhatsApp" 
              className="buyer-whatsapp-icon"
              onClick={() => window.open("https://wa.me/918917412728", "_blank")}
            />
            <span>+91 89174 12728 (JMI Buyer WhatsApp Support)</span>
          
          </div> */}
          <div className="buyer-support-email">
            <p>Email: <a href="mailto:support@jewelmartindia.com">support@jewelmartindia.com</a></p>
          </div>
          <div className="buyer-support-hours">
            <p><strong>Support Hours:</strong> Monday - Saturday, 9:00 AM - 6:00 PM IST</p>
          </div>
        </section>

        <footer className="buyer-page-footer">
          <p>© {new Date().getFullYear()} JMI Buyer. All rights reserved.</p>
          <p className="buyer-footer-note">
            Protecting your privacy and data is important to us.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default BuyerDeleteAccountPlayStore;